import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { UserContactInfoDto, UserLocationDto } from 'src/Libs/user/api/dto/user.dto'
import fromDtoToModelTransformer from 'src/Libs/user/transformers/from-dto-to-model.transformer'
import { CompanyLocation, CompanyResponse } from 'src/Libs/company-settings/models/company.model'
import { Company, User, UserContactInfo, UserData } from 'src/State/User/UserModel'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { isFlowTheme } from 'src/Libs/utils'
import fromCompanyInfoDtoToModel from 'src/Libs/user/transformers/from-contact-info-dto-to-model.transformer'

interface LanguageSettingsResult {
  lang: AppLang
}

/**
 * Hook to fetch data
 */
export const useFetchUserData = (): {
  fetchUser: (userId: number) => Promise<User>
  fetchUserContactInfo: (userId: number) => Promise<UserContactInfo>
  fetchCompany: (companyId: number) => Promise<Company>
} => {
  const authFetch = useAuthFetchWithResult()

  /**
   * Fetches User
   */
  const fetchUser = async (userId: number): Promise<User> => {
    const {
      username,
      Name,
      Vorname,
      ldapEmail,
      Firmen_ID,
      mailNotification,
      level,
      ipin,
      installerCertified,
      ssoId,
      source,
      infinityCertified,
      couponPermission,
      markedAsDeletedAt,
      qntmCertified,
    } = await authFetch<UserData>({
      path: `users/${userId}`,
    })

    const contactData = await authFetch<UserLocationDto>({
      path: `users/${userId}/location`,
    }).then(data => fromDtoToModelTransformer(data))
    const company = Firmen_ID ? await fetchCompany(Firmen_ID) : undefined
    const languageSettings = await authFetch<LanguageSettingsResult>({
      path: `users/${userId}/settings/language`,
    })

    return {
      id: userId,
      userData: {
        ID: userId,
        username,
        Name,
        Vorname,
        ldapEmail,
        mailNotification,
        ipin,
        level,
        installerCertified,
        ssoId,
        source,
        infinityCertified,
        couponPermission,
        language: languageSettings.lang,
        markedAsDeletedAt,
        qntmCertified,
      },
      contactData,
      company,
    }
  }

  const fetchUserContactInfo = async (userId: number): Promise<ReturnType<typeof fromCompanyInfoDtoToModel>> => {
    return authFetch<UserContactInfoDto>({
      path: `users/${userId}/contact-information`,
    }).then(data => fromCompanyInfoDtoToModel(data))
  }

  const fetchCompany = async (companyId: number): Promise<Company> => {
    if (isFlowTheme) return {} as Company
    const { Name, Manager, NDA_submit, shopPermission, showroom, Ust_ID, partnerNumber } = await authFetch<CompanyResponse>({
      path: `users/companies/${companyId}`,
    })
    const companyLocation = await authFetch<CompanyLocation>({
      path: `users/companies/${companyId}/location`,
    })

    return {
      id: companyId,
      Name,
      Manager,
      NDA_submit,
      shopPermission,
      contactData: { ...companyLocation },
      vatNumber: `${Ust_ID || ''}`,
      doDisplayInShowroom: Boolean(showroom),
      partnerNumber,
    } as Company
  }

  return { fetchUser: fetchUser, fetchUserContactInfo: fetchUserContactInfo, fetchCompany: fetchCompany }
}

export const updateUser = async (user: User): Promise<User> => {
  return user
}
